/**
 * Payment return handler to fix endless loading issues after payment
 * This handles the redirect back from payment providers to the PWA
 */

/**
 * Stores the current app state before redirecting to payment
 * @param {Object} state - The state to store (order ID, return URL, etc.)
 */
export function storePaymentState(state) {
  try {
    localStorage.setItem('payment_pending_state', JSON.stringify({
      ...state,
      timestamp: Date.now(),
      returnUrl: window.location.href
    }));
  } catch (error) {
    console.error('Failed to store payment state:', error);
  }
}

/**
 * Clears the stored payment state
 */
export function clearPaymentState() {
  try {
    localStorage.removeItem('payment_pending_state');
  } catch (error) {
    console.error('Failed to clear payment state:', error);
  }
}

/**
 * Gets the stored payment state
 * @returns {Object|null} The stored state or null if none exists
 */
export function getPaymentState() {
  try {
    const stateJson = localStorage.getItem('payment_pending_state');
    if (!stateJson) return null;

    const state = JSON.parse(stateJson);

    // Check if the state is too old (more than 1 hour)
    if (Date.now() - state.timestamp > 60 * 60 * 1000) {
      clearPaymentState();
      return null;
    }

    return state;
  } catch (error) {
    console.error('Failed to get payment state:', error);
    return null;
  }
}

/**
 * Checks if the current page load is a return from payment
 * @param {Object} options - Options for checking
 * @param {Array<string>} options.paymentReturnParams - URL parameters that indicate a payment return
 * @returns {boolean} True if this is a return from payment
 */
export function isPaymentReturn(options = {}) {
  const { paymentReturnParams = ['reference', 'trxref', 'transaction_id', 'tx_ref', 'payment_intent'] } = options;

  // Check URL parameters for payment return indicators
  const urlParams = new URLSearchParams(window.location.search);
  const hasPaymentParam = paymentReturnParams.some(param => urlParams.has(param));

  // Check if we have a pending payment state
  const hasPendingState = !!getPaymentState();

  return hasPaymentParam || hasPendingState;
}

/**
 * Checks if the current page load is a return from payment and handles it
 * This function is used to detect payment returns and break the endless loading loop
 * @returns {boolean} True if this is a return from payment
 */
export function checkPaymentReturn() {
  // Check if we're returning from a payment
  const isReturningFromPayment = isPaymentReturn();

  if (isReturningFromPayment) {
    // Get URL parameters to determine payment status
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const isSuccessful = status ?
      ['success', 'successful', 'approved'].includes(status.toLowerCase()) :
      !urlParams.has('canceled') && !urlParams.has('failed');

    // Get the payment state
    const paymentState = getPaymentState();

    // If we're in standalone PWA mode and returning from payment
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone ||
                        document.referrer.includes('android-app://');

    if (isStandalone && paymentState) {
      // Clear the payment state to prevent future detection
      clearPaymentState();

      // Return true to indicate we're returning from payment
      return true;
    }
  }

  return isReturningFromPayment;
}

/**
 * Handles the return from payment provider
 * @param {Object} options - Options for handling the return
 * @param {Function} options.onSuccess - Callback for successful payment
 * @param {Function} options.onError - Callback for failed payment
 * @param {Function} options.onComplete - Called regardless of success/failure
 * @returns {Promise<Object>} The result of the payment verification
 */
export async function handlePaymentReturn(options = {}) {
  const { onSuccess, onError, onComplete } = options;

  if (!isPaymentReturn()) {
    return { success: false, message: 'Not a payment return' };
  }

  try {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference') ||
                     urlParams.get('trxref') ||
                     urlParams.get('transaction_id') ||
                     urlParams.get('tx_ref') ||
                     urlParams.get('payment_intent');

    // Get stored state
    const state = getPaymentState();

    // Determine if payment was successful based on URL parameters
    // This is a simplified check - in a real app, you should verify with your backend
    const isSuccessful = urlParams.has('status') ?
                        ['success', 'successful', 'approved'].includes(urlParams.get('status').toLowerCase()) :
                        !urlParams.has('canceled') && !urlParams.has('failed');

    // Create result object
    const result = {
      success: isSuccessful,
      reference,
      state,
      params: Object.fromEntries(urlParams.entries())
    };

    // Call appropriate callback
    if (isSuccessful && onSuccess) {
      await onSuccess(result);
    } else if (!isSuccessful && onError) {
      await onError(result);
    }

    // Clear the stored state
    clearPaymentState();

    // Call complete callback
    if (onComplete) {
      await onComplete(result);
    }

    return result;
  } catch (error) {
    console.error('Error handling payment return:', error);

    if (onError) {
      await onError({ success: false, error });
    }

    // Clear the stored state even on error
    clearPaymentState();

    if (onComplete) {
      await onComplete({ success: false, error });
    }

    return { success: false, error };
  }
}

/**
 * Initializes payment return handling for the app
 * Call this during app initialization
 * @param {Object} options - Options for initialization
 * @param {Function} options.onPaymentReturn - Called when a payment return is detected
 */
export function initPaymentReturnHandling(options = {}) {
  const { onPaymentReturn } = options;

  // Check if this is a payment return
  if (isPaymentReturn()) {
    // Handle the payment return
    ({
      onComplete: (result) => {
        if (onPaymentReturn) {
          onPaymentReturn(result);
        }

        // If we have a stored return URL and we're in a PWA, redirect to it
        // This helps fix the endless loading issue
        const state = getPaymentState();
        if (state && state.returnUrl) {
          const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                              window.navigator.standalone ||
                              document.referrer.includes('android-app://');

          if (isStandalone) {
            // Add a timestamp to force a fresh load
            const url = new URL(state.returnUrl);
            url.searchParams.set('_ts', Date.now());
            window.location.replace(url.toString());
          }
        }
      }
    });
  }
}
